import { IButtonStyles, ICommandBarStyles, mergeStyleSets } from "@fluentui/react"
import { DateTime } from "luxon"
import { FieldType } from "../components/Shared/DetailsListFilterCallout"
import { getCurrentUserContactId } from "../hooks/generalHooks"
import { makeCRMApiCall } from "../services/apiService"
import { EmployeeRelationship, IEnrollmentRecord } from "../types/IEnrollmentRecord"
import { IHouseholdMemberEnrollmentManager } from "../types/IHouseholdMember"
import { ITreatyYear } from "../types/ITreatyYear"
import { StyleHelper } from "./StyleHelper"

export const thisIsDevEnvironment = process.env.NODE_ENV === "development"

export const IS_ADMIN = (window.top as any)?.roles?.contains("Administrators")

export const baseUrl =
	(window.top as any).portalBackEndUrl ||
	(thisIsDevEnvironment ? `http://localhost:5003` : window.location.hostname.includes("-sandbox") ? "https://acmg-portal-dev.azurewebsites.net" : "https://acmg-portal.azurewebsites.net")

export const loadScript = (url: string) => {
	return new Promise<void>((resolve, reject) => {
		const existingScriptEl = document.querySelectorAll(`script[src='${url}']`)
		if (existingScriptEl.length > 0) {
			resolve()
		} else {
			const script = document.createElement("script") as any
			script.type = "text/javascript"
			if (script.readyState) {
				//IE
				script.onreadystatechange = function () {
					if (script.readyState === "loaded" || script.readyState === "complete") {
						script.onreadystatechange = null
						resolve()
					}
				}
			} else {
				//Others
				script.onload = function () {
					resolve()
				}
			}
			script.onerror = function () {
				reject()
			}
			script.src = url
			document.getElementsByTagName("head")[0].appendChild(script)
		}
	})
}

export function getFieldDisplayValue(fieldInfo: { Value: any; Type: FieldType }, defaultValue?: string): string {
	let fieldValue: string = defaultValue || ""
	if (fieldInfo) {
		switch (fieldInfo.Type) {
			case FieldType.User: {
				const user = typeof fieldInfo.Value === "object" ? fieldInfo.Value : {}
				fieldValue = user ? `${user.Name || ""}`.trim() : fieldValue
				break
			}
			case FieldType.Date: {
				fieldValue = fieldInfo.Value ? safeFormatDateValue(fieldInfo.Value, "L") : fieldValue
				break
			}
			case FieldType.DateTime: {
				fieldValue = fieldInfo.Value ? safeFormatDateValue(fieldInfo.Value, "MM/DD/YYYY h:mm:ss: a") : fieldValue
				break
			}
			case FieldType.Currency: {
				fieldValue = fieldInfo.Value
					? Number(fieldInfo.Value)?.toLocaleString("en-US", {
							style: "currency",
							currency: "USD"
						})
					: fieldValue
				break
			}
			case FieldType.Number: {
				fieldValue = Number(fieldInfo.Value)?.toLocaleString() || fieldValue
				break
			}
			case FieldType.Boolean: {
				fieldValue = toBoolean(fieldInfo.Value) ? "Yes" : "No"
				break
			}
			default: {
				fieldValue = fieldInfo.Value || fieldValue
				if ((fieldValue as any) instanceof Date || (fieldValue as any) instanceof DateTime) {
					fieldValue = safeFormatDateValue(fieldValue, "L")
				}
			}
		}
	}
	return fieldValue
}

export function getObjectValue(object: any, key: string, fieldType?: FieldType): any {
	let value = object[key]
	if (fieldType === FieldType.Custom) {
		value = object
	} else if (fieldType === FieldType.Date) {
		try {
			if (typeof value === "string") {
				value = safeFormatDateValue(value, "ISO")
			} else if (typeof value === "object" && value instanceof Date) {
				value = safeFormatDateValue(value, "ISO")
			}
		} catch (error) {
			console.error("Error getting object value", error)
			value = ""
		}
	}
	return value
}

export function sortDetailsList(a: any, b: any, key: string, fieldType: FieldType, direction: "asc" | "desc" = "asc", customValueFunc?: (value: any) => any): number {
	// Special handling for renewal date column from management dashboard
	if (key === "cw_renewaldate") {
		key = "cw_coveragestartdate"
	}
	const aValue = customValueFunc ? customValueFunc(a) : getObjectValue(a, key, fieldType)
	const bValue = customValueFunc ? customValueFunc(b) : getObjectValue(b, key, fieldType)
	const isDescending = direction === "desc"

	if (fieldType === FieldType.Date) {
		// Normal date handling for other date columns
		const aDate = aValue === "" ? undefined : safeParseDateTime(aValue)
		const bDate = bValue === "" ? undefined : safeParseDateTime(bValue)

		if (!aDate && !bDate) return 0
		if (!aDate) return isDescending ? 1 : -1
		if (!bDate) return isDescending ? -1 : 1

		if (aDate < bDate) return isDescending ? 1 : -1
		if (aDate > bDate) return isDescending ? -1 : 1
		return 0
	} else {
		if (aValue < bValue) return isDescending ? 1 : -1
		if (aValue > bValue) return isDescending ? -1 : 1
		return 0
	}
}

export function toBoolean(value: unknown): boolean {
	return ["true", "yes", "on", "1", 1, true].includes(String(value).toLowerCase())
}

/**
 * Formats a date value of some kind to a string with the given format.
 * @param value - The date value to format.
 * @param format - The format to use for the date.
 * @param defaultValue - The default value to return if the date value is invalid.
 * @returns A string representing the date.
 */
export function safeFormatDateValue(value: unknown, format: string, defaultValue = "Invalid Date"): string {
	const date = safeParseDateTime(value)
	if (date) {
		switch (format) {
			case "ISO":
			case "I":
				return date.toISO()
			case "L":
				return date.toLocaleString(DateTime.DATE_SHORT)
			default:
				return date.toFormat(format)
		}
	}
	return defaultValue
}

/**
 * Converts a date value of some kind to a DateTime object.
 * @param value - The date value to convert.
 * @param defaultValueOnError - The default value to return if the date value is invalid.
 * @returns A DateTime object representing the date.
 */
export function safeParseDateTime(value: unknown, defaultValueOnError?: DateTime): DateTime {
	const _tryParseStringAsJSDate = (value: unknown): DateTime => {
		const date = new Date(String(value))
		if (date.toString() !== "Invalid Date") {
			return DateTime.fromJSDate(date)
		} else {
			throw new Error("Invalid date")
		}
	}

	try {
		if (value instanceof DateTime) {
			return value
		} else if (value instanceof Date) {
			return DateTime.fromJSDate(value)
		} else if (typeof value === "string") {
			const dateTime = DateTime.fromISO(String(value))
			if (dateTime.isValid) {
				return dateTime
			} else {
				return _tryParseStringAsJSDate(value)
			}
		} else {
			return _tryParseStringAsJSDate(value)
		}
	} catch (error) {
		console.error("Error converting date to Date:", error)
		return defaultValueOnError
	}
}

export const maskAsDate = (value: string): string => {
	const digits = value.replace(/\D/g, "")

	let formatted = digits
	if (digits.length >= 1 && parseInt(digits[0]) > 1) {
		formatted = "0" + digits
	}

	if (formatted.length <= 2) return formatted
	if (formatted.length <= 4) return `${formatted.slice(0, 2)}/${formatted.slice(2)}`
	return `${formatted.slice(0, 2)}/${formatted.slice(2, 4)}/${formatted.slice(4, 8)}`
}

export function getTreatyYears(startYear = 2015): ITreatyYear[] {
	const currentYear = new Date().getFullYear()
	const years: ITreatyYear[] = []
	let index = currentYear - startYear + 1
	for (let year = currentYear; year >= startYear; year--) {
		const endDate = new Date(year + 1, 11, 1)
		endDate.setDate(endDate.getDate() - 1)
		years.push({ endDate: endDate, startDate: new Date(year, 11, 1), treatyYearId: index })
		index--
	}
	return years.sort((a, b) => (a.endDate > b.endDate ? 1 : -1))
}

export const appAjax = async (ajaxOptions: any) => {
	return (window.top as any).webapi.safeAjax(ajaxOptions).fail(function (response: any) {
		if (response.responseJSON) {
			const message = `Error: ${response.responseJSON.error.message}, Error Details: ${response.responseJSON.error?.innererror?.message || "Unknown"}`
			console.log(message)
			//throw new Error(message);
		} else {
			console.log("WEB API is not available")
			//throw new Error("Error: Web API is not available...");
		}
	})
}

export const commandBarItemStyles: Partial<IButtonStyles> = {
	root: {
		backgroundColor: "#0078d4",
		selectors: {
			":hover": {
				backgroundColor: "#EAEAEA",
				color: "#333333 !important"
			},
			icon: {
				minWidth: 16
			}
		}
	}
}

export const cbStyles: Partial<ICommandBarStyles> = {
	root: {
		padding: 0,
		backgroundColor: StyleHelper.cloudwellLightTheme.palette.neutralLight,
		height: "auto"
	}
}

export const loadIndustryCodes = async (): Promise<any[]> => {
	const industriesResponse = await makeCRMApiCall(`cw_industries`)
	return industriesResponse.value
}

export const getErrorText = (error: any): string => {
	let retVal = "We encountered an error."
	if (typeof error === "string") {
		error = { message: error }
	}
	if (typeof error === "object") {
		if (error.message) {
			if (error.isHttpRequestError) {
				try {
					const odataError = JSON.parse(error?.message.substr(error?.message.indexOf("::>") + 4))
					const message = odataError["odata.error"]?.message?.value
					retVal += ` ${message}`
				} catch (ex) {
					retVal += ` Unable to identify error. Please use Submit Feedback in the Menu to report this issue.`
					error(error)
				}
			} else {
				retVal += ` ${error.message}`
			}
		} else if (error.responseJSON) {
			retVal += error.responseJSON.error?.innererror?.message || error.responseJSON.error?.message || "Unknown error"
		} else {
			retVal += ` ${JSON.stringify(error)}`
		}
	}
	return retVal
}

export const getRelationshipValueFromFamilyRelationshipString = (relationship: string): string => {
	let retVal = "-1" //uknown
	if (relationship === "EMPLOYEE") {
		retVal = "0"
	} else if (relationship === "CHILD") {
		retVal = "1"
	} else if (relationship === "SPOUSE") {
		retVal = "2"
	}
	return retVal
}

export const getSSN = (ssn: string): string => {
	if (ssn.length === 9) {
		return "*****" + ssn.substring(5, 9)
	}
	return ssn
}

export const getRelationshipFormattedFromFamilyRelationshipValue = (relationship: number): EmployeeRelationship => {
	let retVal: EmployeeRelationship = "Unknown"
	switch (relationship) {
		case 0:
			retVal = "Employee"
			break
		case 1:
			retVal = "Child"
			break
		case 2:
			retVal = "Spouse"
			break
	}
	return retVal
}

export const getChoiceFormattedFromCoverageTypeValue = (coverageType: string): string => {
	let retVal = "Unknown"
	switch (Number(coverageType)) {
		case 121640000:
			retVal = "EMP/SPOUSE (ES)"
			break
		case 121640001:
			retVal = "WAIVED"
			break
		case 121640002:
			retVal = "FAMILY (EF)"
			break
		case 121640003:
			retVal = "SPOUSE ONLY"
			break
		case 121640004:
			retVal = "EMP/CHILD (E1)"
			break
		case 121640005:
			retVal = "CHILD ONLY"
			break
		case 121640007:
			retVal = "EMP ONLY (EE)"
			break
		case 121640008:
			retVal = "EMP/CHILDREN (EC)"
			break
	}
	return retVal
}

export const getGenderValueFromGender = (gender: number): string => {
	let retVal = "Uknown"
	switch (Number(gender)) {
		case 121640000:
			retVal = "Male"
			break
		case 121640001:
			retVal = "Female"
			break
		case 121640002:
			retVal = "Transgender"
			break
		case 121640003:
			retVal = "Non-binary/non-conforming"
			break
		case 121640004:
			retVal = "Prefer not to respond"
			break
	}

	return retVal
}

export const getRaterTierFormattedFromCoverageTypeValue = (coverageType: number): string => {
	let retVal = "Unknown"
	switch (Number(coverageType)) {
		case 121640000:
			retVal = "ES"
			break
		case 121640001:
			retVal = "WAIVED"
			break
		case 121640002:
			retVal = "EF"
			break
		case 121640003:
			retVal = "SO"
			break
		case 121640004:
			retVal = "E1"
			break
		case 121640005:
			retVal = "CO"
			break
		case 121640007:
			retVal = "EE"
			break
		case 121640008:
			retVal = "EC"
			break
	}
	return retVal
}

export const getCurrentUserEmail = async (): Promise<string> => {
	const contactId = getCurrentUserContactId()
	const contact = await makeCRMApiCall(`contacts(${contactId})?$select=emailaddress1`)
	const contactEmail = contact.emailaddress1
	return contactEmail
}

export const addQualifyingEvent = (contactEmail: string, description: string, employeeId: string) => {
	const qBody = {
		"cw_Employee@odata.bind": `/cw_employees(${employeeId})`,
		cw_name: description,
		cw_username: contactEmail,
		cw_updatedate: new Date().toISOString()
	}
	return makeCRMApiCall(`cw_qualifyingevents`, "POST", JSON.stringify(qBody))
}

const EntityEditControlBlockClassNames = {
	root: "EntityEditControlBlockRoot",
	child: "EntityEditControlBlock"
}

interface IEntityEditControlBlockClassNames {
	root: string
	child: string
}

export const getClassNames = (): IEntityEditControlBlockClassNames => {
	return mergeStyleSets({
		root: [EntityEditControlBlockClassNames.root, {}],
		child: [
			EntityEditControlBlockClassNames.child,
			{
				cursor: "pointer !important",
				position: "absolute",
				top: "-3px",
				selectors: {
					[`.${EntityEditControlBlockClassNames.root} button&`]: {
						position: "absolute"
					},
					"& div": {
						paddingLeft: "12px"
					},
					"& * ": {
						cursor: "pointer !important"
					}
				}
			}
		]
	})
}

export const getEnrollementRecordsFromHouseholdMembers = (householdMembers: IHouseholdMemberEnrollmentManager[]): IEnrollmentRecord[] => {
	const innerEnrollments: IEnrollmentRecord[] = []
	householdMembers.forEach(householdMember => {
		innerEnrollments.push({
			key: householdMember.cw_householdmembersid,
			relationship: householdMember.cw_relationship === undefined ? "Unknown" : getRelationshipFormattedFromFamilyRelationshipValue(Number(householdMember.cw_relationship)),
			employee_id: householdMember.employee_id,
			record: householdMember,
			birthday: householdMember.cw_birthdate && new Date(householdMember.cw_birthdate),
			coverageTier: getChoiceFormattedFromCoverageTypeValue(householdMember.cw_healthcarecoveragetype),
			dept: householdMember.cw_department,
			name: householdMember.cw_name,
			firstName: householdMember.cw_firstname,
			lastName: householdMember.cw_lastname,
			middleName: householdMember.cw_middlename,
			gender: householdMember.cw_gender,
			emailaddress: householdMember.emailaddress,
			insEffDate: householdMember.cw_insurancestartdate && new Date(householdMember.cw_insurancestartdate),
			insTermDate: householdMember.cw_insuranceenddate ? new Date(householdMember.cw_insuranceenddate) : undefined,
			milestone: "TBD",
			planSelection: householdMember.plan_name,
			signature: "TBD",
			SSN: householdMember.cw_ssn,
			startCoverage: "YES",
			cw_householdmembersid: householdMember.cw_householdmembersid,
			cw_hhzip: householdMember.cw_hhzip
		})
	})
	return innerEnrollments
}

export const sortHouseHoldMembers = (householdMembers: IHouseholdMemberEnrollmentManager[]): IHouseholdMemberEnrollmentManager[] => {
	return householdMembers.sort((a, b) => {
		let retVal = -1
		if (a.employee_id === b.employee_id) {
			if (Number(a.cw_relationship) !== Number(b.cw_relationship)) {
				retVal = Number(a.cw_relationship) === 0 && Number(b.cw_relationship) !== 0 ? -1 : Number(a.cw_relationship) === 2 && Number(b.cw_relationship) !== 0 ? -1 : 1 //sort employee, spouse, child
			}
		} else {
			retVal = a.cw_lastname < b.cw_lastname ? -1 : 1 //group by families
		}
		return retVal
	})
}
